import { observe, observable, action, computed, when } from 'mobx';
import { subscriptionStatusObservable } from 'core/client';

import { analytics } from 'core/analytics';
import { clientStore } from 'apps/client/app';

import { CadFile } from 'libs/rendering/gltf/api/cadFile';
import { Cad3dViewer } from 'libs/rendering/gltf/api/cad3dViewer';
import { isIterable } from 'libs/rendering/gltf/api/wasmObservable';
import {
  ConversionProblemError,
  LogFailError,
  GltfViewerException,
} from 'libs/rendering/gltf/api/errors';

import PropTypes from 'prop-types/prop-types';

import { GltfConnection } from './api/connection';

import { UiModes, UiPosition, PinConfig } from './config';

const uniqueID = (() => {
  let id = 0;
  return () => {
    id += 1;
    return id;
  };
})();

const propsSchema = { isPreview: PropTypes.bool };

export class ViewerControl {
  // wasm module
  _scriptPathLocator = null;
  @observable.ref wftyModule = null;
  // Cad3dViewer
  @observable.ref viewer = null;
  @observable canvasSet = false;
  @observable canvasChanged = false;
  // connection
  @observable offline = false;
  _onFileReady = null;
  @observable gltfConnection = null;
  @observable downloadPerc = 0.0;
  // - the FileApp that we queried from the graphql api
  @observable file = null;
  // - the CadFile is the wasm representation of the FileApp once
  // it is loaded into the viewer, its easy to imagine a sceneario
  // where, while loading, the cadFile and file refer to different
  // files
  @observable.ref cadFile = null;
  // - conversionProblem is meant to indicate a conversionProblem beyond
  // a failed conversion, if there is an unforseen problem in the viewer
  // when parsing a successful conversion, then this flag should be set
  @observable conversionProblem = false;
  // - viewerFailed is set when LogFail is called, it will cause the viewer
  // to stop rendering, and display an error message, it is considered
  // recoverable and display a Reload button to the user
  // - if the tab crashes we can't do anything about it
  @observable viewerFailed = false;
  // - the viewer is kept alive in the background, so flags
  // like viewerReady, cadReady will be true as long as a
  // valid file has been loaded before, so we need another
  // flag from which we can tell if the viewer is currently
  // visible or hidden, this is the first one being set
  // and only indicates if the viewer is visible or not
  // - it is different from the other flags because this is set
  // to false in Unmount, so could also be called viewerMounted
  @observable viewerVisible = false;
  // - this is set in startRendering, so once this is set we're rendering
  // and can execute opengl, but the CadFile is still loading
  @observable viewerReady = false;
  // - this is set before cadReady, it indicates we loaded the file and
  // the assembly tree(?) is ready (UserActionCadAssemblyReady)
  @observable assemblyReady = false;
  // - cadReady is set to true once the entire file has been loaded the
  // viewer is rendering, once this flag is set the viewer should be
  // completely ready (UserActionCadRequestFinished)
  @observable cadReady = false;
  // UI
  // LightModal
  @observable _lightModalStart = false;
  @observable lightModalVisible = false;
  @observable lightModalContentIndex = 0;
  // Ui "context" (assembly, geometry...)
  @observable uiMode = UiModes.collapsed;
  // left panel resize (constant by now)
  @observable leftPanelWidth = UiPosition.panel;
  // right click menu
  @observable contextMenuOpened = false;
  // annotations
  @observable toolBarPinExpanded = false;
  @observable expandedPinId = null;
  @observable hoveredPinId = null;
  // HACK: I needed this because the isPreview flag is now
  // inside the wasm, but I can't access that while the viewer
  // is initializing
  // TODO: make the c++ use the viewerControl isPreview so
  // we can make this one the one and only isPreview flag
  @observable viewerControlIsPreview = false;
  // lastLoadedId is so we can compare the file.id we are about
  // to load to the lastLoadedId, if they are the same we can
  // just unhide the viewer and don't need to load the file
  @observable lastLoadedId = undefined;

  reportedExceptions = undefined;

  constructor(contextManager = null, isPreview = false) {
    const id = uniqueID();
    this.wrapperId = `gltfWrapper${id}`;
    this.canvasId = `gltfCanvas${id}`;
    this.obsMap = new Map();
    this.reportedExceptions = new Map();

    // connection/subscription
    this.offline = false;
    this.disposers = [];

    // observe the client status
    const subscriptionStatusDisposer = observe(
      subscriptionStatusObservable,
      change => {
        // check if the client is offline
        this.offline = change.newValue === 'disconnected';
      },
    );
    this.disposers.push(subscriptionStatusDisposer);

    // - scene-ready promise (for previews)
    // - there is another readyPromise that emscripten creates, but we did
    // our own thing here, sceneReadyPromise is resolved in
    // cad3dViewer.handleUserEvent by calling this.___sceneReadyPromiseResolve
    this.sceneReadyPromise = new Promise(resolve => {
      this.___sceneReadyPromiseResolve = resolve;
    });

    // pass this to contextManger...
    if (contextManager) {
      contextManager.SetViewerControl(this);
    }

    this.viewerControlIsPreview = isPreview;
  }

  // internal
  async _loadWasm() {
    if (this.wftyModule) return;

    // load emscriptem module
    const gltfModuleURL =
      MODE === 'development'
        ? `${STATIC_URL_BASE}/assets/gltf/gltf_web_viewer.js?deploy=${WASM_DEPLOY}`
        : `${STATIC_WASM_URL}/${this.viewerSemver}/gltf_web_viewer.js?deploy=${WASM_DEPLOY}`;

    const emscriptenModule = await import(
      /* webpackIgnore: true */ gltfModuleURL
    );
    if (!emscriptenModule) {
      this.logWarn('cannot download emscriptem module', gltfModuleURL);
      return;
    }
    const loadWftyModule = emscriptenModule.default;

    // load wasm module
    const [gltfWasm, gltfData] = [
      MODE === 'development'
        ? `${STATIC_URL_BASE}/assets/gltf/gltf_web_viewer.wasm?deploy=${WASM_DEPLOY}`
        : `${STATIC_WASM_URL}/${this.viewerSemver}/gltf_web_viewer.wasm?deploy=${WASM_DEPLOY}`,
      MODE === 'development'
        ? `${STATIC_URL_BASE}/assets/gltf/gltf_web_viewer.data?deploy=${WASM_DEPLOY}`
        : `${STATIC_WASM_URL}/${this.viewerSemver}/gltf_web_viewer.data?deploy=${WASM_DEPLOY}`,
    ];
    this._scriptPathLocator = {
      locateFile(path) {
        if (path.endsWith('.wasm')) {
          return gltfWasm;
        }
        return gltfData;
      },
    };

    // - this is not used anymore, to enable it use the
    // ABORT_ON_WASM_EXCEPTIONS=1 flag
    this._scriptPathLocator.onAbort = what => {
      const scopingFunction = scope => {
        scope.setTag('subsystem', 'wasm-unhandled-exception');
        scope.setTag('exception', what);
      };
      this.LogException(what, scopingFunction);
    };

    this.wftyModule = await loadWftyModule(this._scriptPathLocator);
  }

  async _loadCadViewer(props = null) {
    // load wasm module if not loaded yet
    await this._loadWasm();

    if (!this.viewer) {
      this.viewer = new Cad3dViewer(this.wftyModule, this);

      const version = this.wasmVersion?.Describe();
      this.logInfo(`viewer version: ${version}`);
    } else {
      // - I have to call makeWasmObservables so its guaranteed these
      // get re-created, the constructor already creates them but it
      // only runs once, then we reuse the existing Cad3dViewer and
      // only load a new file
      // - so this is the other side of the viewerControl.deleteWasmObservables()
      // call in canvas3D.js which deletes wasm observables before loading a new
      // file
      this.viewer.makeWasmObservables();
    }

    // set defaults
    if (props) {
      try {
        PropTypes.checkPropTypes(propsSchema, props);
        if (props.isPreview) {
          this.viewer.isPreview = props.isPreview;
          this.logInfo(`viewer isPreview: ${this.viewer.isPreview}`);
        }
        // TODO: perspective/ortho, faces/wireframe, upAxis... etc
      } catch (ex) {
        this.logWarn(`cannot apply viewer props: ${ex.toString()}`);
      }
    }
  }

  async _loadCadFile(fileApp, props = null) {
    if (!fileApp) return;

    // load the viewer if not loaded yet
    await this._loadCadViewer(props);

    const viewId = this?.lastLoadedId;
    this.lastLoadedId = fileApp.id;

    const newFileId = fileApp?.id;
    if (newFileId && (!this.cadFile || viewId !== newFileId)) {
      this._clearConnection();

      // file changed,
      // initialize the connection, and start downloading the gltf and buffers
      this._onFileReady = when(
        () => this.file && this.file.conversionGltfjson?.status === 'SUCCESS',
        () => {
          const newConnection = this._initGltfConnection();
          if (newConnection) {
            // - I changed setCadFile so we can pass null, that way the viewer
            // CadFile can be reset to its initial state
            this._unloadCadFile();
            this.viewer?.SetCadFile(null);

            // sometimes this.file.conversionGltfjson?.status is set multiple times
            const cadFile = new CadFile(this, newFileId);
            cadFile.initNotePad();

            // set default canvas if not set
            if (!this.canvasSet) {
              this.SetCanvas();
            }

            // - setCadFile sets a viewer._cadFile, not this.cadFile, so we can set
            // it and then call _unloadCadFile after to delete the old cadFile, then
            // set it to the newly created one (ended up not doing that)
            this.viewer.SetCadFile(cadFile);
            this.cadFile = cadFile;

            if (this.wftyModule?.canvas) {
              // - this is where we start rendering something to the canvas, so
              // if we're here, we've loaded a file and everything is fine so we
              // can start rendering
              // - keep in mind we're async here
              this.startRendering();
              this.viewer.updateCanvas();
              this.viewer.clearCanvas();
              // - instead of making the viewer visible here, I set viewerVisible = true
              // in the Cad3dViewer.handleUserEvent, same place where cadReady = true,
              // setTimeout(() => {
              //   this.viewerVisible = true;
              // }, 500);
            }
          }
        },
      );
    }
  }

  _initGltfConnection = () => {
    if (!this.gltfConnection) {
      if (this._scriptPathLocator) {
        const viewId = this.file.id;
        this.gltfConnection = new GltfConnection(viewId, this);
        this.gltfConnection.setModule(this._scriptPathLocator);
      } else {
        this.LogException('no wasm module path locator');
      }

      // needed for wasm callbacks
      window.globalGltfConnection = this.gltfConnection;
      return true;
    }
    return false;
  };

  _unloadCadFile = () => {
    this.cadFile?.destroy();
    this.cadFile = null;
    this.cadReady = false;
    this.assemblyReady = false;

    // delete wasm objects
    // TODO
  };

  // TODO: the lifetime of the wasm observables could be controlled in c++,
  // try making destructors in all the relevant c++ classes which then call
  // some js code to remove their wasm observables
  // - this is the pendant to cad3dViewer.makeWasmObservables(), it is here
  // so it can be called from the canvas3D useEffect for loading files into
  // the viewer
  // - it is also called in Unload()
  deleteWasmObservables = () => {
    // delete wasm observables...
    this.obsMap.clear();
  };

  _clearConnection = () => {
    this.offline = false;
    this._onFileReady = null;
    this.gltfConnection = null;
    window.globalGltfConnection = null;
    this.downloadPerc = 0.0;
  };

  startRendering = () => {
    this.viewer.startRendering();
    this.viewerReady = true;
  };

  @computed get isPreview() {
    return this.viewerControlIsPreview || this.viewer?.isPreview;
  }

  @computed get viewerSemver() {
    return this.file?.conversionGltfjson?.viewerSemver || 'latest';
  }

  @computed get wasmVersion() {
    if (this.wftyModule) {
      return new this.wftyModule.VersionEmbind();
    }
    return null;
  }

  @computed get userName() {
    if (clientStore && clientStore?.signedInProfile) {
      return clientStore.signedInProfile.username?.slice(1);
    }
    return null;
  }

  loseContext = () => {
    const { ctx } = this.wftyModule;
    if (ctx) {
      ctx.getExtension('WEBGL_lose_context').loseContext();
    }
  };

  @computed get isSinglePartFile() {
    if (this.conversionProblem || this.viewerFailed) {
      return undefined;
    }
    if (this.viewerVisible && this.assemblyReady) {
      return this.cadFile?.assembly?.InstanceCount() === 1;
    }
    return undefined;
  }

  @action SetCanvas = (newWrapperId = null, newCanvasId = null) => {
    if (!document) {
      this.LogException('cannot set viewer canvas, null document');
      return;
    }

    // set new IDs
    if (!newWrapperId) {
      newWrapperId = this.wrappedId;
    } else {
      this.wrapperId = newWrapperId;
    }
    if (!newCanvasId) {
      newCanvasId = this.canvasId;
    } else {
      this.canvasId = newCanvasId;
    }

    const thereWasCanvas = this.viewer ? this.viewer.isCanvasSet : false;

    // apply IDs
    const viewerHolder = document.getElementById(this.wrapperId);
    const canvasHolder = document.getElementById(this.canvasId);
    if (
      viewerHolder &&
      canvasHolder &&
      viewerHolder.tagName.toLowerCase() === 'div' &&
      canvasHolder.tagName.toLowerCase() === 'canvas' &&
      viewerHolder.contains(canvasHolder)
    ) {
      this.wftyModule.canvas = canvasHolder;

      // create proper WebGL context for the viewer
      this.wftyModule.createContext(canvasHolder, true, true, {
        antialias: true, // this fixes jaggy lines (if client browser + GPU manges antialiasing in webGL...)
        premultipliedAlpha: false, // this fixes odd WebGL transparency behaviour
        preserveDrawingBuffer: true, // to take snapsots
      });

      this.canvasSet = this.viewer.setCanvas(viewerHolder, canvasHolder);
      if (this.canvasSet) {
        this.canvasChanged = true;
        if (thereWasCanvas) {
          // We were rendering in a different canvas before, so
          //  we need to re-create the viewer GPU state...
          // Stop rendering...
          this.viewer.stop();
          this.viewerReady = false;
          this.cadReady = false;
          const reloadStatus = this.viewer.reload();
          if (reloadStatus === 0) {
            // resume rendering
            this.viewer?.settings?.reportWasmChange();
            this.cadReady = true;
            this.SyncSelectionMode();
            this.startRendering();
          } else {
            this.LogException(
              'cannot reload viewer after WebGL context regeneration',
            );
          }
        }

        // add canvas event listeners
        viewerHolder.addEventListener(
          'wheel',
          e => {
            e.preventDefault();
          },
          {
            passive: false,
          },
        );

        // prevent the default behaviour (so it recovers from a lost context)
        canvasHolder.addEventListener(
          'webglcontextlost',
          e => {
            e.preventDefault();
            if (!this.viewer) {
              return;
            }
            canvasHolder.style.visibility = 'hidden';
            if (this.viewer) {
              this.viewerReady = false;
              this.cadReady = false;
              this.viewer.stop();
            }
          },
          false,
        );

        canvasHolder.addEventListener(
          'webglcontextrestored',
          () => {
            if (!this.viewer) {
              return;
            }
            // re-create the viewer...
            try {
              if (this.viewer) {
                const reloadStatus = this.viewer.reload();
                if (reloadStatus === 0) {
                  // resume rendering
                  this.viewer.settings.reportWasmChange();
                  this.cadReady = true;
                  this.SyncSelectionMode();

                  // setting the style.visibility like so works around the fact that
                  // viewerVisible is used to indicate if the Viewer is mounted/unmounted,
                  // which is different from contextlost/restored that we're handling here,
                  // so thats why I left this here instead of using viewerVisible because
                  // I didn't want the viewerVisible to be triggered when the Viewer is
                  // not mounted
                  canvasHolder.style.visibility = 'visible';
                  if (this.viewerVisible) {
                    this.startRendering();
                  }
                } else {
                  // cannot recreate state, reload the page...
                  this.LogException('cannot reload the viewer, refreshing');
                  window.location.reload();
                }
              }
            } catch (ex) {
              this.LogException('cannot recreate webGL context, refreshing');
              // cannot recreate state, reload the page...
              window.location.reload();
            }
          },
          false,
        );
      } else {
        this.LogException('cannot set viewer canvas');
      }
    } else {
      this.LogException(
        `cannot find HTML elements by id ${this.wrapperId} ${this.canvasId}`,
      );
    }
  };

  async loadCadFile(fileApp, props = null) {
    if (!fileApp) {
      this.logWarn('cannot load null FileApp');
      return;
    }

    await this._loadCadFile(fileApp, props);
  }

  @action Unload = () => {
    // unload canvas
    this.viewer?.setCanvas(null, null);
    this.canvasSet = false;
    this.canvasChanged = false;
    if (this.wftyModule) {
      this.wftyModule.canvas = null;
    }

    this._clearConnection();

    // unload file
    this.conversionProblem = false;
    this._unloadCadFile();

    this.deleteWasmObservables();

    // UI defaults
    this._userStart = false;
    this.uiMode = UiModes.collapsed; // Ui "context" (assembly, geometry...)
    this.lightModalVisible = false;
    this.lightModalContentIndex = 0;
    this.leftPanelWidth = UiPosition.panel; // left panel resize width size
    this.contextMenuOpened = false;
    this.toolBarPinExpanded = false;
    this.expandedPinId = null;
    this.hoveredPinId = null;

    // destroy the viewer
    this.viewer?.destroy();
    this.viewer = null;

    this.lastLoadedId = null;
    this.viewerVisible = false;
    this.viewerReady = false;

    this.lastLoadedId = null;

    this.logWarn('viewer destroyed');
  };

  @action Unmount = () => {
    const heapSize = this.heap8SizeMb().toFixed(2);
    this.logWarn(`unmounting viewer, heapSize: ${heapSize}Mb`);

    this.viewerVisible = false;

    const { size } = this.reportedExceptions;
    if (size > 0) {
      this.logInfo(`clearing out ${size} reported exceptions:`);
      this.reportedExceptions.forEach((_, k) => {
        this.logInfo(`clearing: ${k}`);
      });
      this.reportedExceptions.clear();
    }

    // - when this.conversionProblem is true, then we have
    // a problem with a conversion, but the viewer is fine
    // - when this.viewerFailed is true, then there was
    // some exception in the wasm and we probably need to completely
    // unload the viewer
    // - to prevent to keep showing the error message, we need to
    // reset the viewerFailed = false, but also reset the
    // file to null to force the useEffect in canvas3D to run,
    // should we try to go back to the same file without reloading
    // - I also added lastLoadedId = null because I added startRendering
    // to the useEffect that sets viewerVisible = true when the file
    // changes, but thought that if the file previously error'ed,
    // then we should not call startRendering, and setting lastLoadedId
    // = null should prevent that
    if (this.conversionProblem || this.viewerFailed) {
      this.conversionProblem = false;

      this.lastLoadedId = null;
      this.file = null;
      this.assemblyReady = false;
    }

    if (this.viewerFailed) {
      this.deleteWasmObservables();
      this.viewer.destroy();
      this.viewer = null;

      this.viewerFailed = false;
    } else {
      this.viewer?.stop();
      // - need to set explode mode to false, otherwise we can go
      // back to the viewer and load a single part file, and have
      // explode mode active in a single part file with no way
      // to turn it off
      this.ToggleExplodeMode(false);
      // - turn off annotation mode
      this.DisableAnnotationMode();
      // - close the context menu
      this.CloseContextMenu();
      // - reset download state so it starts at 0% next time we
      // load something
      this.downloadPerc = 0;

      // - only preview viewers really unload when leaving the page
      if (this.isPreview) {
        this.Unload();
      }
    }
  };

  @action Mount = (file, status) => {
    if (status !== 'SUCCESS' && this.lastLoadedId === file?.id) {
      this.Unload();
    }
    if (this.lastLoadedId === file?.id) {
      // - when lastLoadedId === file.id, we just reuse the already running
      // viewer, no need to load anything, so just set it visible again
      // and refresh the canvas
      this.viewerVisible = true;
      this.viewer.navCube.visible = true;
      this.startRendering();
    } else {
      // - disable section planes only when loading a new file
      this.DisableSectionMode(false);
      // - if lastLoadedId !== file.id, then we will load a new file, the
      // viewerVisible flag will be set in this.LoadCadFile then
      // once the viewer is ready to render
      // - we don't know if loading will be successful, and it is async
      // - cadReady and viewerReady are flags that are set once the the CadFile
      // is ready, and once the viewer is ready, so before triggering loading
      // a new CadFile we need to reset them here so all logic that relies on
      // them knows the viewer is "not ready" while loading
      this.cadReady = false;
      this.assemblyReady = false;
      this.viewerReady = false;
    }
    // - set the file in viewerControl, there is a useEffect in Canvas3D that will
    // take care of the loading
    this.file = file;
  };

  @action RequestSignIn = () => {
    if (
      this.gltfConnection &&
      (!this.gltfConnection.sanityCheck || this.downloadPerc < 0.99)
    ) {
      // connection failed before completion, cancel it
      this._clearConnection();
    }

    if (clientStore?.routerStore) {
      // redirect to sign-in with next param, so
      //  it gets back to the viewer right away
      clientStore.routerStore.navigate('home.signIn', {
        next: clientStore.next(),
      });
    }
  };

  /*
    Async Initialization
  */

  // viewer wasm callback
  @action SetAssemblyReady(value) {
    if (this.assemblyReady !== value) {
      this.assemblyReady = value;
    }
  }

  /*
    Viewer render
  */

  isContextLost() {
    const canvasHolder = document.getElementById(this.canvasId);
    if (!canvasHolder) return false;
    let gl = canvasHolder.getContext('webgl2');
    if (gl == null) {
      gl = canvasHolder.getContext('webgl1');
      if (gl == null) {
        gl = canvasHolder.getContext('webgl');
        if (gl == null) {
          return false;
        }
      }
    }
    return gl.isContextLost();
  }

  @action ForceRender = () => {
    this.cadFile?.assembly?.TriggerStateChange();
  };

  /*
    UI mode
  */

  @action UpdateUiMode = mode => {
    if (this.uiMode !== mode) {
      this.uiMode = mode;
      // expand/update left menu
      if (mode === UiModes.assembly) {
        // expanding the assembly browser, so expand root node
        const root = this.cadFile.assembly.GetProduct(0);
        if (root !== null) {
          if (!root.expanded) {
            root.expanded = true;
            const subRoot = root.GetChild(0);
            if (subRoot && !subRoot.IsSubassembly()) {
              subRoot.expanded = true;
            }
          }
        }
      }
    } else {
      // collapse left menu
      this.uiMode = UiModes.collapsed;
    }

    // viewer (wasm) uimode must be updated too...
    this.viewer.mode = this.uiMode;

    // selection mode changes depends on current left menu
    this.SyncSelectionMode();
  };

  // uiMode <--> selection mode sync
  @action SyncSelectionMode = () => {
    const selManager = this.viewer?.selectionManager;
    if (!selManager) return;

    const camera = this.viewer?.settings?.CurrentCamera();
    if (!camera) return;

    const defaultSelMode = camera.GetDefaultSelMode(this.viewer.mode);
    selManager.SetSelectionMode(defaultSelMode);
  };

  /*
    Selection manager
  */

  // dbl click timer utils
  @action SetTimer = (newTimer, trigger) => {
    this.timer = newTimer;
    if (trigger === null && this.timerTrigger != null) {
      // act on last...
      if (this.timerTrigger.highlighted) {
        this.timerTrigger.highlighted = false;
        this.timerTrigger.selected = false;
      }
    }
    this.timerTrigger = trigger;
  };

  @action SetPreventNext = flag => {
    this.preventNext = flag;
  };

  @action SetHighlightBehaviour = (mode, item) => {
    const { engine } = this.viewer;
    if (mode === 'on-front') {
      if (item == null) {
        // all on-front
        engine.selectionOnFront = true;
      } else {
        // just an item on-front
        engine.SetShowOnFront(item._wasm);
      }
    } else {
      // nothing on-front
      engine.selectionOnFront = false;
      engine.SetShowOnFront(null);
    }
  };

  /*
    Pin Notes
  */

  pinTargetType = noteId => {
    const note = this.cadFile?.notePad?.GetNote(noteId);
    const target = note?.GetLink().GetTarget();
    return target ? (target.IsInstance() ? 'part' : 'feature') : null;
  };

  pinBelongsToCamera = (note, camera) => {
    if (!note) return false;
    if (!this.viewer) return false;

    if (!camera) return false;
    if (camera.GetIndex() === 0) return true;

    const target = note.GetLink().GetTarget();
    const isTargetDrawing = target.IsTypeDrawing();

    if (camera.IsDrawing()) {
      const drawing = camera.GetDrawing();
      const currSheetIx = drawing ? drawing.currentSheetIx : -1;
      return (
        isTargetDrawing &&
        camera.GetInstanceIndex() === target.instanceHitIx &&
        currSheetIx === target.brepIx
      );
    }

    if (camera.IsInstance()) {
      return (
        !isTargetDrawing && camera.GetInstanceIndex() === target.instanceHitIx
      );
    }

    if (camera.IsSubAssembly()) {
      if (!isTargetDrawing) {
        const { assembly } = this.cadFile;
        const instance = assembly?.GetInstance(target.instanceHitIx);
        const prod = camera.GetSubAssembly();
        if (prod && instance) {
          return prod.IsInstanceBelow(instance._wasm);
        }
      }
      return false;
    }

    return !isTargetDrawing;
  };

  @action HighlightPinTarget = noteId => {
    const note = this.cadFile?.notePad?.GetNote(noteId);
    if (!note) return;
    const target = note.GetLink().GetTarget();

    if (note && note.IsOk()) {
      if (this.viewer.selectionManager) {
        if (!target.IsInstance()) {
          // pin
          target.SetIndirectPin();
          this.viewer.selectionManager.ClearHighlight();
          this.viewer.selectionManager.AddHighlight(target._wasm);
        } else {
          // indirect
          target.SetIndirect();
          this.viewer.selectionManager.ClearHighlight();
          this.viewer.selectionManager.AddHighlight(target._wasm);
        }
        this.hoveredPinId = noteId;
      }
    }
  };

  @action UnHighlightPinTarget = () => {
    this.viewer?.selectionManager?.ClearHighlight();
    this.hoveredPinId = null;
  };

  @action SelectPinTarget = target => {
    // check if observable or not...
    let tgtWasm = target;
    if (tgtWasm._wasm) tgtWasm = target._wasm;

    if (!target.IsInstance()) {
      target.SetIndirectPin();
    } else {
      target.SetIndirect();
    }
    this.viewer.selectionManager.AddSelection(tgtWasm);
  };

  @action UnSelectPinTarget = target => {
    // check if observable or not...
    let tgtWasm = target;
    if (tgtWasm._wasm) tgtWasm = target._wasm;

    this.viewer.selectionManager.DeleteSelectionItem(tgtWasm);
  };

  @action TogglePinExpand = noteId => {
    const note = this.cadFile?.notePad?.GetNote(noteId);
    if (!note) return;
    const target = note.GetLink().GetTarget();

    // clear selection before highlight the pin target
    this.viewer.selectionManager.ClearSelection();

    if (note && note.IsOk() && noteId !== this.expandedPinId) {
      this.expandedPinId = note.id;
      // select the pin target
      this.SelectPinTarget(target._wasm);

      if (!this.viewer?.selectionManager.IsSelectionModeLink()) {
        // not in pin addition mode...
        const currentCamera = this.viewer.settings.CurrentCamera();
        if (currentCamera.Is3D() && !currentCamera.IsPlaneXY()) {
          // animation to pin note camera
          this.viewer.settings.SetNoteTarget(note._wasm);
        }
      }
    } else {
      this.expandedPinId = null;
      // unselect the pin target
      this.UnSelectPinTarget(target._wasm);
    }
  };

  @action ClearNotes() {
    const nNotes = this.cadFile?.notePad?.NoteCount();
    if (nNotes > 0) {
      // delete from global map
      const notes = this.cadFile.notePad.GetNotes();
      if (notes) {
        const keys = notes.keys();
        if (isIterable(keys)) {
          // eslint-disable-next-line no-restricted-syntax
          for (const noteKey of keys) {
            const note = this.cadFile.notePad.GetNote(noteKey);
            if (note) {
              const mapKey = note._mapKey;
              this.obsMap.delete(mapKey);
            }
          }
        }
        // delete from wasm
        this.cadFile.notePad.Clear();
      }
    }
  }

  /*
    Right-click menu
  */

  @computed get hoveredCadItem() {
    if (this.contextMenuItem) return this.contextMenuItem;

    const selManager = this.viewer?.selectionManager;
    const assembly = this.cadFile?.assembly;

    if (assembly && selManager) {
      // get target from last selection
      const lastIx = selManager.HighlightCount() - 1;
      const selTarget = selManager.GetHighlight(lastIx);
      const instance = selTarget
        ? assembly.GetInstance(selTarget.instanceHitIx)
        : null;

      if (instance) {
        if (selTarget?.IsInstance()) {
          this.contextMenuItem = instance;
          return instance;
        }
      }
      // TODO: faces, edges... etc
    }
    return null;
  }

  @computed get hoveredUserCamera() {
    const session = this.viewer?.session;
    if (session) {
      return session.highUser;
    }
    return null;
  }

  @action CloseContextMenu = () => {
    this.contextMenuItem = null;
    this.contextMenuOpened = false;
  };

  /*
    Tab Manager
  */

  cadItemByPartId = partId => {
    if (partId && this.cadReady) {
      const { assembly } = this.cadFile;
      if (assembly) {
        // get all instances of this body
        const instance = assembly.GetInstanceByBodyId(partId);
        if (instance) {
          // inspect the first one...
          return instance;
        }
      }
    }
    return null;
  };

  @action InspectPartById = partId => {
    const instance = this.cadItemByPartId(partId);
    if (instance) {
      this.InspectCadItem(false, instance);
    }
  };

  @action InspectCadItem = (isDoubleClick, cadItem) => {
    if (!cadItem) return;

    if (isDoubleClick === true) {
      clearTimeout(this.timer);
      this.SetTimer(null, null);
      this.SetPreventNext(true);
      this.viewer.settings.FocusItemCamera(cadItem._wasm);
    } else {
      this.viewer.settings.FocusItemCamera(cadItem._wasm);
    }

    // unselect
    this.viewer.selectionManager.ClearHighlight();
    this.SetHighlightBehaviour(null, null);

    const selfTarget = cadItem?.GetSelfTargetItem();
    if (selfTarget) {
      const selIx = this.viewer.selectionManager.ContainsSelectionItemTarget(
        selfTarget._wasm,
      );
      if (selIx >= 0) this.viewer.selectionManager.DeleteSelection(selIx);
    }

    // set default sel/high behavior back
    this.SetHighlightBehaviour(null, null);
  };

  /*
    Camera
  */

  @action FocusCadItem = (cadItem, zoom) => {
    const focusedItem = cadItem ? cadItem._wasm : null;
    this.viewer.settings.SetFocusedItem(focusedItem, zoom);
  };

  @action CameraUpdated = streamPatch => {
    const camera = this.viewer?.settings?.CurrentCamera();
    camera?.reportWasmChange();
    if (streamPatch === true) {
      // report changes to all collaborators...
      this.streamCameraPatch();
    }
  };

  @action ViewUpdated = streamPatch => {
    this.viewer?.navCube?.reportWasmChange();
    this.viewer?.settings?.reportWasmChange();
    const c = this.viewer?.settings?.CurrentCamera();
    c?.reportWasmChange();
    if (streamPatch === true) {
      // report changes to all collaborators...
      this.streamViewPatch();
    }
  };

  @action SelectionUpdated = streamPatch => {
    this.viewer?.selectionManager?.reportWasmChange();
    if (streamPatch === true) {
      // report changes to all collaborators...
      this.streamSelectionPatch();
    }
  };

  /*
    State streaming
  */

  streamCameraPatch = () => {
    // report changes to all collaborators...
    const { session } = this.viewer;
    if (session && session.UserCount() > 0) {
      const mainCamera = this.viewer.settings.GetCamera(0);
      // eslint-disable-next-line no-console
      // console.log('STREAMING -CAMERA- PATCH');
      if (mainCamera) {
        // const patch = mainCamera.ToJson();
      }
    }
  };

  streamViewPatch = () => {
    // report changes to all collaborators...
    const { session } = this.viewer;
    if (session && session.UserCount() > 0) {
      const { settings } = this.viewer;
      // eslint-disable-next-line no-console
      // console.log('STREAMING -VIEW- PATCH');
      if (settings) {
        // const patch = settings.ToJson();
      }
    }
  };

  streamSelectionPatch = () => {
    // report changes to all collaborators...
    const { session } = this.viewer;
    if (session && session.UserCount() > 0) {
      const selManager = this.viewer.selectionManager;
      // eslint-disable-next-line no-console
      // console.log('STREAMING -SELECTION- PATCH');
      if (selManager) {
        // const patch = selManager.ToJson();
      }
    }
  };

  /*
    UI
  */

  @action ExitPinMode = () => {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (!camera) return false;

    const selManager = this.viewer?.selectionManager;
    const selModeLink = selManager?.IsSelectionModeLink();
    const selModeEnum = this.wftyModule?.SelectionMode;

    let somethingToggled = false;
    if (selModeLink) {
      // selecting pin note targets...
      // exit pin target selection mode...
      if (camera.IsInstance()) {
        selManager?.SetSelectionMode(selModeEnum.SelectionModeGeometry);
      } else {
        selManager?.SetSelectionMode(selModeEnum.SelectionModeAssembly);
      }
      somethingToggled = true;
    } else if (this.toolBarPinExpanded) {
      // hide toolBar pin addtition menu
      this.toolBarPinExpanded = false;
      somethingToggled = true;
    } else if (selManager?.GetSelectionCount() > 0) {
      // clear selection...
      selManager?.ClearSelection();
      somethingToggled = true;
    }

    return somethingToggled;
  };

  // TODO: alex - move logic of opening/closing Assembly Tree to uiStore
  getLeftOffset = () => {
    const camera = this.viewer?.settings?.CurrentCamera();

    if (camera?.IsDrawing()) {
      return 0;
    }

    if (this.uiMode !== UiModes.collapsed) {
      return UiPosition.panel;
    }
    return 0;
  };

  _setLightModalContentIndex = ix => {
    this.lightModalContentIndex = typeof ix === 'number' ? ix : 0;
  };

  // wasm callback @ [UserActionCadRequestFinished]
  // @action updateLightModal = () => {
  //   if (localStorage.getItem('mobileGuidelinesLightModal') !== 'true') {
  //     setTimeout(() => {
  //       this._userStart = true;
  //       // This will open on mobile a LightModal with the default content
  //       this._setLightModalContentIndex(0); // No Access message component (index 0)
  //       this.setLightModalVisibility(true);
  //     }, 1000);
  //   }
  // };

  // toggle the LightModal visibility
  // TODO: check usability of this method, remove it after testing
  // @action toggleLightModalVisibility(isOpen) {
  //   if (isOpen !== undefined) {
  //     this.lightModalVisible = isOpen;
  //   } else {
  //     this.lightModalVisible = !this.lightModalVisible;
  //   }
  //
  //   if (
  //     this._lightModalStart === true &&
  //     !this.lightModalVisible &&
  //     this.lightModalContentIndex === 0
  //   ) {
  //     this._setLightModalContentIndex(1);
  //     setTimeout(() => {
  //       this?.toggleLightModalVisibility(true);
  //     }, 100);
  //   }
  //   if (this._lightModalStart === true && !this.lightModalVisible) {
  //     localStorage.setItem('mobileGuidelinesLightModal', 'true');
  //   }
  // }

  @action ToggleModalVisibility() {
    this.lightModalVisible = !this.lightModalVisible;
  }

  @action SetLightModalVisibility(value) {
    this.lightModalVisible = value;
  }

  @action ToggleExplodeMode = value => {
    if (!this.viewer) {
      return;
    }
    if (value !== undefined) {
      this.viewer.engine.SetExplodeRunning(value);
    } else if (this.viewer.engine.ExplodeRuning()) {
      // turning off
      this.viewer.engine.SetExplodeRunning(false);
    } else {
      // turning on
      this.viewer.engine.SetExplodeRunning(true);
    }
  };

  @action DisableClipPlanes = () => {
    if (!this.viewer?.sectionState) {
      return;
    }

    // - the ClipPlanes are on a stack, if you disable the first one the
    // remaining ones will move up, so to disable all of the ClipPlanes
    // we need to disable the first one three times
    // - we could also do 3,2,1 or maybe even 1,2,1 but 1,1,1 makes the
    // most sense
    this.viewer?.sectionState.toggleClip(1);
    this.viewer?.sectionState.toggleClip(1);
    this.viewer?.sectionState.toggleClip(1);
  };

  @action DisableSectionMode = () => {
    if (!this.viewer) {
      return;
    }
    this.viewer.sectionManager.SetActive(false);
    this.DisableClipPlanes();
    this.UpdateUiMode(UiModes.collapsed);
  };

  @action DisableAnnotationMode = () => {
    // close any open temporary pins
    this.TogglePinExpand(PinConfig.pinTempId);
    // hide pin addition menu
    this.toolBarPinExpanded = false;
    // exit addition mode
    this.SyncSelectionMode();
  };

  /*
    Debug / Log
    (output wasm logs into console)
  */

  getExceptionMessage = what => {
    // - "what" comes most likely from a wasm exception, so it
    // should be a string and not an error
    // - we need to pass an Error() to logFail (and sentry), so
    // when we get a string, then it is a wasm exception and we
    // get the actual exception from wasm with getExceptionMessage
    // (which takes a pointer value), and then make an Error()
    // out of that
    if (typeof what === 'string' || what instanceof String) {
      let matches = what.match(/^([0-9]+)$/);
      if (!matches || matches.length === 1) {
        matches = what.match(/unhandled exception: ([0-9]+),/);
      }
      if (matches?.length > 1) {
        const ptr = parseInt(matches[1], 10);
        if (Number.isSafeInteger(ptr)) {
          const msg = this.wftyModule.getExceptionMessage(ptr);
          return msg;
        }
      }
    } else if (Number.isSafeInteger(what)) {
      const msg = this.wftyModule.getExceptionMessage(what);
      return msg;
    }

    return undefined;
  };

  @action LogFail = (what, scopingFunction = () => {}) => {
    this.viewerFailed = true;

    const localScopingFunction = scope => {
      scope.setTag('subsystem', 'wasm-fail');
      scope.setTag('exception', what);
      scopingFunction(scope);
    };

    const msg = this.getExceptionMessage(what);
    let err = what;
    if (msg) {
      err = new LogFailError(msg);
    }
    this.reportError(err, localScopingFunction);
  };

  @action LogConversionProblem = (what, scopingFunction = () => {}) => {
    this.conversionProblem = true;

    const localScopingFunction = scope => {
      scope.setTag('subsystem', 'wasm-conversion-problem');
      scope.setTag('exception', what);
      scopingFunction(scope);
    };

    const msg = this.getExceptionMessage(what);
    let err = what;
    if (msg) {
      err = new ConversionProblemError(msg);
    }
    this.reportError(err, localScopingFunction);
  };

  @action LogException = (what, scopingFunction = () => {}) => {
    const localScopingFunction = scope => {
      scope.setTag('subsystem', 'wasm-exception');
      scope.setTag('exception', what);
      scopingFunction(scope);
    };

    const msg = this.getExceptionMessage(what);
    let err = what;
    if (msg) {
      err = new GltfViewerException(msg);
    }

    if (!this.reportedExceptions.get(err.toString())) {
      this.reportedExceptions.set(err.toString(), true);
      this.reportError(err, localScopingFunction);
    } else {
      this.reportError(err, localScopingFunction);
      this.viewerFailed = true;
    }
  };

  reportError = (err, scopingFunction = () => {}) => {
    const localScopingFunction = scope => {
      scope.setExtra('heap8Size', this.heap8SizeMb());
      scopingFunction(scope);
    };
    analytics.logError(err, localScopingFunction);
  };

  logWarn = what => {
    analytics.addBreadcrumb({
      category: 'gltf-viewer',
      message: what,
      level: 'warn',
    });
    // eslint-disable-next-line no-console
    console.warn(what);
  };

  logInfo = what => {
    analytics.addBreadcrumb({
      category: 'gltf-viewer',
      message: what,
      level: 'info',
    });
    // eslint-disable-next-line no-console
    console.log(what);
  };

  logDebug = what => {
    // eslint-disable-next-line no-console
    console.log(what);
  };

  heap8SizeMb = () => {
    if (this.viewer && this.viewer.module) {
      const heap8SizeMb = this.viewer.module.HEAP8.length / 1000000;
      return heap8SizeMb;
    }
    return -1;
  };

  /*
    debug
  */

  fakeAddCollaborator = () => {
    const settings = this.viewer?.settings;
    const session = this.viewer?.session;
    if (settings && session) {
      const camera = settings.CurrentCamera();
      if (camera) {
        // get fake camera data
        const cameraJsonStr = settings.GetCameraPatch(camera.GetIndex());
        const userId = `fakeUser${new Date().getTime()}`;
        // add fake collaborator...
        session.SetUser(userId, cameraJsonStr);
        const userCount = session.UserCount();
        // eslint-disable-next-line no-console
        console.log(
          'fake collaborator added:',
          userId,
          cameraJsonStr,
          '(',
          userCount,
          'users)',
        );
      }
    }
  };

  fakeRedirect = () => {
    const rfqId = 'UkZROjB4Y2Q0M2Mw';
    clientStore.routerStore.router.navigate('rfq', { rfqId });
  };

  fakeWebGLctxLost = () => {
    if (!this.viewer) return;
    if (!document) return;
    const canvasHolder = document.getElementById(this.canvasId);
    if (!canvasHolder) return;

    let gl = canvasHolder.getContext('webgl2');
    if (gl == null) {
      gl = canvasHolder.getContext('webgl1');
      if (gl == null) {
        gl = canvasHolder.getContext('webgl');
        if (gl == null) {
          console.error('cannot get webGL context');
          return;
        }
      }
    }

    const ext = gl.getExtension('WEBGL_lose_context');
    const restoreSeconds = 3;

    if (!gl.isContextLost()) {
      // eslint-disable-next-line no-console
      console.log(
        'forcing [WEBGL_lose_context]. Restoring it in',
        restoreSeconds,
        'seconds...',
      );
      // force lost...
      ext.loseContext();

      // force restore...
      setTimeout(() => {
        ext.restoreContext();
      }, 1000 * restoreSeconds);
    }
  };

  fakeUploadCadFile = contextManager => {
    contextManager?.QueryCadFile({
      space: '@javierruiz',
      slug: 'testcad',
      filepath: 'Diff Pivot Swivel.SLDASM',
      version: undefined,
    });
  };
}
